if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.submitListener = this.handleSubmit.bind(this);
      this.quantityChangeListener = this.handleChange.bind(this);
      this.variantChangeListener = this.setMaximumInputQuantity.bind(this);
      if (this.hasChildNodes()) this.init();
    }

    connectedCallback() {
      this.init();
      this.attachVariantListener();
      this.hydrateSharedQuantity();
      if (this.compactQuantityMode) this.collapseQuantitySelector();

      if (!this.initialized) {
        this.initFrame = window.requestAnimationFrame(() => {
          this.init();
          this.attachVariantListener();
          this.hydrateSharedQuantity();
          if (this.compactQuantityMode) this.collapseQuantitySelector();
        });
      }
    }

    init() {
      if (this.initialized) return;
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.initialized = true;
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.form.addEventListener('submit', this.submitListener);

        if (this.hasAttribute('data-show-quantity-selector')) {
          this.quantitySelector = this.querySelector('quantity-input');
        }
        if (this.quantitySelector) {
          this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
          this.lastConfirmedQuantity = parseInt(this.quantityInput.value, 10) || 0;
          this.compactQuantityMode = Boolean(this.querySelector('[data-quick-add-count]'));
          if (this.compactQuantityMode) {
            this.updateCartCountBadge(this.lastConfirmedQuantity);
            this.collapseQuantitySelector();
          }
          this.quantitySelector.addEventListener('change', this.quantityChangeListener);
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
      }
    }

    attachVariantListener() {
      if (this.variantListenerAttached) return;
      if (!this.closest('.product-info') && !this.closest('quick-add-drawer')) return;
      document.addEventListener('on:variant:change', this.variantChangeListener);
      this.variantListenerAttached = true;
    }

    hydrateSharedQuantity() {
      if (!this.quantitySelector || !ProductForm.quantitiesByVariant) return;
      const quantity = ProductForm.quantitiesByVariant.get(String(this.dataset.variantId));
      if (quantity === undefined) return;
      this.lastConfirmedQuantity = quantity;
      this.updateQuantityState(quantity);
    }

    disconnectedCallback() {
      if (this.initFrame) window.cancelAnimationFrame(this.initFrame);
      if (this.quantityRevealTimer) window.clearTimeout(this.quantityRevealTimer);
      window.clearTimeout(this.submitFeedbackTimer);
      if (this.variantListenerAttached) {
        document.removeEventListener('on:variant:change', this.variantChangeListener);
        this.variantListenerAttached = false;
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          quantityInput.removeAttribute('max');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          if (dataEl) {
            const inventory = JSON.parse(dataEl.textContent);

            if (inventory) {
              if (inventory.inventory_management && inventory.inventory_quantity > 0 && inventory.inventory_policy !== 'continue') {
                quantityInput.max = inventory.inventory_quantity;

                const currentQuantity = Number(quantityInput.value);
                if (currentQuantity > 1 && currentQuantity > inventory.inventory_quantity) {
                  quantityInput.value = quantityInput.max;
                }
              }
            }
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      // Enable or disable buttons based on the newQuantity relative to max and min
      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    /**
     * Handles 'change' events on the line item (dispatched by the theme).
     * @param {object} evt - Event object.
     */
    applyLineItemChange(detail) {
      if (String(detail.variantId) === this.dataset.variantId) {
        if (!this.quantitySelector || !this.quantityInput) return;
        if (
          detail.quantityRevision
          && !ProductForm.isLatestQuantityRevision(this.dataset.variantId, detail.quantityRevision)
        ) return;
        this.lastConfirmedQuantity = parseInt(detail.newQuantity, 10) || 0;
        this.updateQuantityState(detail.newQuantity);
      }
    }

    /**
     * Handles 'change' events on the quantity-input element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      const requestedQuantity = parseInt(evt.target.value, 10) || 0;
      const quantityRevision = ProductForm.beginQuantityMutation(this.dataset.variantId);
      // Keep the pill and collapsed badge on the shopper's latest intent while
      // Shopify confirms it. A slow response must never reveal the old count
      // when the 2.5-second morph retracts.
      this.updateQuantityState(requestedQuantity);
      this.revealQuantitySelector();
      this.queueQuantityUpdate(requestedQuantity, quantityRevision);
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');
      const activeSubmitBtn = evt.submitter || this.submitBtn;
      const submitButtons = this.getSubmitButtons();

      if (!this.submitBtn || activeSubmitBtn?.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Match fast grocery interfaces: show the quantity control immediately and
      // reconcile it with Shopify after the request completes.
      const previousQuantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) || 0 : 0;
      const isOptimisticAdd = Boolean(
        this.quantitySelector
        && this.quantityInput
        && this.dataset.showQuantitySelector !== 'true'
      );
      const quantityRevision = this.quantitySelector
        ? ProductForm.beginQuantityMutation(this.dataset.variantId)
        : null;

      if (isOptimisticAdd) {
        this.updateQuantityState(previousQuantity + 1);
        this.revealQuantitySelector();
        this.setAttribute('aria-busy', 'true');
      }

      // Disable "Add to Cart" button until submission is complete.
      window.clearTimeout(this.submitFeedbackTimer);
      submitButtons.forEach((button) => {
        button.setAttribute('aria-disabled', 'true');
        button.classList.remove('is-success');
        button.classList.add('is-loading');
      });

      const formData = new FormData(this.form);
      if (isOptimisticAdd) formData.set('quantity', '1');
      let sections = 'cart-icon-bubble';
      const shouldOpenCartDrawer = Boolean(
        this.cartDrawer
        && (theme.settings.afterAtc === 'drawer'
          || (theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md))
      );
      if (shouldOpenCartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Get all quick order lists on the page
      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          // sections.push({
          //   id: `quick-order-list_${sectionId}`,
          //   section: sectionId,
          //   selector: `#quick-order-list_${sectionId}`
          // });
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let addSucceeded = false;
      try {
        this.pendingAddRequest = fetch(theme.routes.cartAdd, fetchRequestOpts);
        const response = await this.pendingAddRequest;
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);
        addSucceeded = true;
        this.lastConfirmedQuantity = previousQuantity + 1;

        if (isOptimisticAdd) {
          this.removeAttribute('aria-busy');
        }

        if (theme.settings.afterAtc === 'page') {
          window.location.href = theme.routes.cart;
        } else {
          // Update cart icon count.
          if (
            !quantityRevision
            || ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)
          ) ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // Update cart drawer contents.
          if (this.cartDrawer && shouldOpenCartDrawer) {
            this.cartDrawer.renderContents(
              data,
              !quickAddDrawer
            );

            // Update the quick order lists on the page
            if (quickOrderSections.length) {
              quickOrderSections.forEach((quickOrderSection) => {
                const sectionEl = document.getElementById(quickOrderSection.id);
                if (!sectionEl) return;

                const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                el.innerHTML = ProductForm.getElementHTML(
                  data.sections[quickOrderSection.section],
                  quickOrderSection.selector
                );
              });
            }
          } else if (this.cartDrawer) {
            // Keep the drawer accurate without making the Add interaction wait
            // for its much larger rendered section.
            this.cartDrawer.refreshCartDrawer();
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refreshCartItems();
              }
            }
          }
        }

        // Cart JSON is useful for cross-card synchronisation, but it must not
        // delay the visible Add response or re-enable the control.
        fetch(`${theme.routes.cart}.js`)
          .then((cartResponse) => {
            if (!cartResponse.ok) throw new Error(cartResponse.status);
            return cartResponse.json();
          })
          .then((newCartData) => {
            if (
              quantityRevision
              && !ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)
            ) return;
            const confirmedQuantity = newCartData.items.reduce((total, item) => (
              item.variant_id === data.variant_id ? total + item.quantity : total
            ), 0) || (previousQuantity + 1);
            this.lastConfirmedQuantity = confirmedQuantity;
            ProductForm.updateCartItemCount(newCartData.item_count);

            document.dispatchEvent(new CustomEvent('on:line-item:change', {
              detail: {
                cart: newCartData,
                variantId: data.variant_id,
                oldQuantity: previousQuantity,
                newQuantity: confirmedQuantity,
                quantityRevision
              }
            }));

            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id
              }
            }));
            ProductForm.announce(
              `Added ${this.dataset.productTitle || 'item'}; ${confirmedQuantity} in basket.`
            );
            if (this.quantityInput && activeSubmitBtn === document.activeElement) {
              window.requestAnimationFrame(() => this.quantityInput.focus({ preventScroll: true }));
            }
          })
          .catch((error) => console.log(error)); // eslint-disable-line
      } catch (error) {
        console.log(error); // eslint-disable-line

        if (
          isOptimisticAdd
          && ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)
        ) {
          this.updateQuantityState(previousQuantity);
          this.removeAttribute('aria-busy');
        }

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg ? this.errorMsg.textContent : error.message
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } finally {
        // Re-enable 'Add to Cart' button.
        submitButtons.forEach((button) => {
          if (addSucceeded) button.classList.add('is-success');
          else button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
        });
        this.pendingAddRequest = null;
        if (isOptimisticAdd) {
          this.removeAttribute('aria-busy');
        }
        this.submitFeedbackTimer = window.setTimeout(() => {
          submitButtons.forEach((button) => {
            button.classList.remove('is-loading');
            button.classList.remove('is-success');
          });
        }, 250);
      }
    }

    getSubmitButtons() {
      const buttons = [this.submitBtn];
      if (this.form?.id) {
        document.querySelectorAll(`[name="add"][form="${CSS.escape(this.form.id)}"]`)
          .forEach((button) => buttons.push(button));
      }
      return [...new Set(buttons.filter(Boolean))];
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    static updateCartItemCount(count) {
      if (typeof count !== 'number') return;
      const label = `${count} ${count === 1 ? 'item' : 'items'}`;
      document.querySelectorAll('.js-cart-item-count').forEach((element) => {
        element.textContent = label;
      });
    }

    static announce(message) {
      let region = document.getElementById('product-form-status');
      if (!region) {
        region = document.createElement('div');
        region.id = 'product-form-status';
        region.className = 'visually-hidden';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        document.body.appendChild(region);
      }

      region.textContent = '';
      window.requestAnimationFrame(() => { region.textContent = message; });
    }

    static beginQuantityMutation(variantId) {
      if (!ProductForm.quantityRevisions) ProductForm.quantityRevisions = new Map();
      const key = String(variantId);
      const revision = (ProductForm.quantityRevisions.get(key) || 0) + 1;
      ProductForm.quantityRevisions.set(key, revision);
      return revision;
    }

    static isLatestQuantityRevision(variantId, revision) {
      if (!revision || !ProductForm.quantityRevisions) return true;
      return ProductForm.quantityRevisions.get(String(variantId)) === revision;
    }

    updateQuantityState(newQuantity) {
      this.quantityInput.value = newQuantity;
      this.quantitySelector.currentQty = newQuantity;
      if (this.compactQuantityMode) {
        this.updateCartCountBadge(newQuantity);
        if (newQuantity <= 0) this.collapseQuantitySelector();
      } else {
        this.setAttribute('data-show-quantity-selector', newQuantity > 0 ? 'true' : 'false');
      }

      const minusButton = this.quantitySelector.querySelector('.btn--minus');
      const plusButton = this.quantitySelector.querySelector('.btn--plus');
      if (minusButton && plusButton) {
        ProductForm.setQuantityButtonState(null, this.quantityInput, minusButton, plusButton);
      }
    }

    updateCartCountBadge(quantity) {
      const badge = this.querySelector('[data-quick-add-count]');
      if (!badge) return;

      const count = parseInt(quantity, 10) || 0;
      badge.textContent = count;
      badge.hidden = count <= 0;
    }

    revealQuantitySelector() {
      if (
        !this.compactQuantityMode
        || !this.quantitySelector
        || (parseInt(this.quantityInput.value, 10) || 0) <= 0
      ) return;
      window.clearTimeout(this.quantityRevealTimer);
      this.setAttribute('data-show-quantity-selector', 'true');
      this.scheduleQuantitySelectorCollapse();
    }

    scheduleQuantitySelectorCollapse() {
      if (!this.compactQuantityMode) return;
      window.clearTimeout(this.quantityRevealTimer);
      this.quantityRevealTimer = window.setTimeout(() => {
        this.collapseQuantitySelector();
      }, 2500);
    }

    collapseQuantitySelector() {
      if (!this.compactQuantityMode) return;
      window.clearTimeout(this.quantityRevealTimer);
      this.setAttribute('data-show-quantity-selector', 'false');
    }

    queueQuantityUpdate(quantity, quantityRevision) {
      this.queuedQuantityUpdate = { quantity, quantityRevision };
      if (this.quantityUpdatePromise) return this.quantityUpdatePromise;

      this.quantityUpdatePromise = (async () => {
        while (this.queuedQuantityUpdate) {
          const nextUpdate = this.queuedQuantityUpdate;
          this.queuedQuantityUpdate = null;
          await this.updateQuantity(nextUpdate.quantity, nextUpdate.quantityRevision);
        }
      })().finally(() => {
        this.quantityUpdatePromise = null;
      });

      return this.quantityUpdatePromise;
    }

    async updateQuantity(quantity, quantityRevision) {
      this.quantitySelector.setAttribute('aria-disabled', 'true');
      const lineErrors = this.querySelector('.js-product-form-errors');
      let updateSucceeded = false;
      let previousConfirmedQuantity = this.lastConfirmedQuantity;

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      fetchRequestOpts.body = JSON.stringify({
        id: this.dataset.variantId,
        quantity,
        sections: 'cart-icon-bubble',
        sections_url: window.location.pathname
      });

      try {
        // A shopper can adjust the optimistic quantity immediately; queue the
        // cart change behind the still-running Add request when necessary.
        if (this.pendingAddRequest) {
          const addResponse = await this.pendingAddRequest;
          if (!addResponse.ok) throw new Error(addResponse.status);
          previousConfirmedQuantity = this.lastConfirmedQuantity;
        }

        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);
        updateSucceeded = true;
        const confirmedQuantity = (data.items || []).reduce((sum, item) => (
          String(item.variant_id) === String(this.dataset.variantId)
            ? sum + Number(item.quantity)
            : sum
        ), 0);
        // Always retain the newest server-confirmed value. A later queued
        // request may fail and must roll back to this quantity, not the value
        // from before the queue started.
        this.lastConfirmedQuantity = confirmedQuantity;

        if (ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)) {
          if (data.sections && data.sections['cart-icon-bubble']) {
            ProductForm.updateCartIcon(data);
          }
          ProductForm.updateCartItemCount(data.item_count);
          document.dispatchEvent(new CustomEvent('on:line-item:change', {
            detail: {
              cart: data,
              variantId: Number(this.dataset.variantId),
              oldQuantity: previousConfirmedQuantity,
              newQuantity: confirmedQuantity,
              quantityRevision
            }
          }));
          ProductForm.announce(
            `${this.dataset.productTitle || 'Item'} quantity updated to ${confirmedQuantity}.`
          );
        }

        if (
          ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)
          && window.location.pathname === theme.routes.cart
        ) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems && cartItems.dataset.empty !== 'true') cartItems.refreshCartItems();
        }

        if (lineErrors) {
          lineErrors.innerHTML = '';
          lineErrors.hidden = true;
        }
      } catch (error) {
        const formattedError = /^[0-9]+$/.test(error.message)
          ? theme.strings.cartError
          : ProductForm.formatErrorMessage(error.message);

        if (this.closest('.card--product')) {
          ProductForm.showGlobalError(formattedError);
          if (lineErrors) lineErrors.hidden = true;
        } else if (lineErrors) {
          lineErrors.textContent = formattedError;
          lineErrors.hidden = false;
          window.clearTimeout(this.quantityErrorTimer);
          this.quantityErrorTimer = window.setTimeout(() => {
            lineErrors.hidden = true;
          }, 7500);
        }
        console.log(error); // eslint-disable-line

        if (ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)) {
          this.updateQuantityState(this.lastConfirmedQuantity);
        }
      } finally {
        if (
          updateSucceeded
          && ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)
        ) this.updateQuantityState(this.lastConfirmedQuantity);
        if (ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)) {
          this.quantitySelector.removeAttribute('aria-disabled');
        }
        if (
          this.cartDrawer
          && ProductForm.isLatestQuantityRevision(this.dataset.variantId, quantityRevision)
        ) this.cartDrawer.refreshCartDrawer();
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    static formatErrorMessage(error) {
      const message = String(error || '').trim();
      const availabilityMatch = message.match(
        /^Only\s+(\d+)\s+items?\s+was added to your cart due to availability\.?$/i
      );

      if (!availabilityMatch) return message;

      const availableCount = Number(availabilityMatch[1]);
      return `Only ${availableCount} ${availableCount === 1 ? 'item is' : 'items are'} available. Your cart has been updated.`;
    }

    static showGlobalError(error) {
      let toast = document.querySelector('.product-card-error-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'product-card-error-toast';
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        document.body.appendChild(toast);
      }

      toast.textContent = ProductForm.formatErrorMessage(error);
      toast.hidden = false;
      window.clearTimeout(ProductForm.errorToastTimer);
      ProductForm.errorToastTimer = window.setTimeout(() => {
        toast.hidden = true;
      }, 7500);
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      window.clearTimeout(this.errorMsgTimer);
      this.errorMsg.hidden = !error;
      if (error) {
        const errorArray = Array.isArray(error) ? error : [error];
        if (this.closest('.card--product')) {
          this.errorMsg.hidden = true;
          ProductForm.showGlobalError(errorArray.map((err) => ProductForm.formatErrorMessage(err)).join(' '));
          return;
        }

        this.errorMsg.innerHTML = '';
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', ProductForm.formatErrorMessage(err));
        });

        this.errorMsgTimer = window.setTimeout(() => {
          this.errorMsg.hidden = true;
        }, 7500);
      }
    }
  }

  ProductForm.quantitiesByVariant = new Map();
  document.addEventListener('on:line-item:change', (evt) => {
    const detail = evt.detail || {};
    if (detail.variantId === undefined) return;
    const variantId = String(detail.variantId);
    const quantity = parseInt(detail.newQuantity, 10) || 0;
    ProductForm.quantitiesByVariant.set(variantId, quantity);
    document.querySelectorAll(`product-form[data-variant-id="${CSS.escape(variantId)}"]`)
      .forEach((form) => form.applyLineItemChange(detail));
  });

  customElements.define('product-form', ProductForm);
}
